import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  Testability,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Datastore, DatastoreDocument } from '@freelancer/datastore';
import {
  Thread,
  ThreadsCollection,
  UserBalance,
  UserBalancesCollection,
  UserInfoCollection,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { PerformanceTracking } from '@freelancer/tracking';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CalloutPlacement, CalloutSize } from '@freelancer/ui/callout';
import { ContainerSize } from '@freelancer/ui/container';
import { IconColor } from '@freelancer/ui/icon';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import {
  filter,
  map,
  publishReplay,
  refCount,
  startWith,
} from 'rxjs/operators';

export enum NavLink {
  DASHBOARD = '/dashboard',
  BROWSE = '/navigation/browse',
  MESSAGES = '/messages',
  UPDATES = '/navigation/updates',
  GIVE = '/give',
  POST_PROJECT = '/post-project',
  USER = '/navigation/user',
}

@Component({
  selector: 'app-navigation-primary',
  template: `
    <fl-bit class="NavigationRoot" flTrackingSection="NavigationPrimary">
      <fl-container class="NavigationContainer" [size]="size">
        <fl-bit class="NavigationItem">
          <fl-button
            class="NavigationItemBtn"
            flTrackingLabel="Logo"
            [ngClass]="{
              IsActive: (currentUrl$ | async) === NavLink.DASHBOARD
            }"
            [display]="'flex'"
            [link]="'/'"
          >
            <fl-logo
              class="NavigationItemLogo"
              [size]="LogoSize.SMALL"
              [flHideMobile]="true"
              [backgroundColor]="
                darkMode ? BackgroundColor.DARK : BackgroundColor.LIGHT
              "
            ></fl-logo>
            <fl-logo
              [size]="LogoSize.ICON"
              [flHideDesktop]="true"
              [flHideTablet]="true"
              [backgroundColor]="
                darkMode ? BackgroundColor.DARK : BackgroundColor.LIGHT
              "
            ></fl-logo>
          </fl-button>
        </fl-bit>

        <fl-callout
          class="NavigationItem"
          [edgeToEdge]="true"
          [hideCloseButton]="true"
          [placement]="CalloutPlacement.BOTTOM"
          [size]="CalloutSize.LARGE"
        >
          <fl-callout-trigger class="NavigationItemInner">
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="Browse"
              [flShowMobile]="true"
              [display]="'flex'"
              [link]="NavLink.BROWSE"
              [ngClass]="{ IsActive: (currentUrl$ | async) === NavLink.BROWSE }"
              (click)="$event.stopPropagation()"
            >
              <ng-container
                *ngTemplateOutlet="browseButtonContent"
              ></ng-container>
            </fl-button>
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="Browse"
              [flHideMobile]="true"
              [display]="'flex'"
            >
              <ng-container
                *ngTemplateOutlet="browseButtonContent"
              ></ng-container>
            </fl-button>

            <ng-template #browseButtonContent>
              <fl-bit class="NavigationItem-inner">
                <fl-icon
                  [flHideTablet]="this.showGiveButton$ | async"
                  [color]="IconColor.INHERIT"
                  [name]="'ui-search'"
                  [flMarginRightTablet]="Margin.XXSMALL"
                ></fl-icon>
                <fl-text
                  i18n="Navigation primary tab label"
                  [color]="FontColor.INHERIT"
                  [flHideMobile]="true"
                  [size]="TextSize.XXSMALL"
                >
                  Browse
                </fl-text>
              </fl-bit>
            </ng-template>
          </fl-callout-trigger>

          <fl-callout-content> <app-browse></app-browse> </fl-callout-content>
        </fl-callout>

        <fl-callout
          class="NavigationItem"
          [flHideMobile]="true"
          [edgeToEdge]="true"
          [hideCloseButton]="true"
          [placement]="CalloutPlacement.BOTTOM"
          [size]="CalloutSize.MEDIUM"
        >
          <fl-callout-trigger class="NavigationItemInner">
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="MyProjects"
              [display]="'flex'"
            >
              <fl-bit class="NavigationItem-inner">
                <fl-icon
                  [flHideTablet]="this.showGiveButton$ | async"
                  [color]="IconColor.INHERIT"
                  [name]="'ui-computer-outline'"
                  [flMarginRightTablet]="Margin.XXSMALL"
                ></fl-icon>
                <fl-text
                  i18n="Navigation primary tab label"
                  [color]="FontColor.INHERIT"
                  [size]="TextSize.XXSMALL"
                >
                  My Projects
                </fl-text>
              </fl-bit>
            </fl-button>
          </fl-callout-trigger>

          <fl-callout-content>
            <app-my-projects></app-my-projects>
          </fl-callout-content>
        </fl-callout>

        <fl-callout
          class="NavigationItem"
          [edgeToEdge]="true"
          [hideCloseButton]="true"
          [placement]="CalloutPlacement.BOTTOM"
          [size]="CalloutSize.MEDIUM"
        >
          <fl-callout-trigger class="NavigationItemInner">
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="Messages"
              [flShowMobile]="true"
              [display]="'flex'"
              [link]="NavLink.MESSAGES"
              [ngClass]="{
                IsActive: (currentUrl$ | async) === NavLink.MESSAGES
              }"
              (click)="$event.stopPropagation()"
            >
              <ng-container *ngTemplateOutlet="messages"></ng-container>
            </fl-button>
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="Messages"
              [flHideMobile]="true"
              [display]="'flex'"
            >
              <ng-container *ngTemplateOutlet="messages"></ng-container>
            </fl-button>
          </fl-callout-trigger>

          <fl-callout-content>
            <app-messages></app-messages>
          </fl-callout-content>
        </fl-callout>

        <ng-template #messages>
          <fl-bit class="NavigationItem-inner">
            <fl-bit
              class="IconContainer"
              [flHideTablet]="this.showGiveButton$ | async"
              [flMarginRightTablet]="Margin.XXSMALL"
            >
              <fl-icon
                [color]="IconColor.INHERIT"
                [name]="'ui-chat-outline'"
              ></fl-icon>
              <fl-unread-indicator
                *ngIf="messagesUnread$ | async"
                ariaLabel="unread messages"
              ></fl-unread-indicator>
            </fl-bit>
            <fl-text
              i18n="Navigation primary tab label"
              [flHideMobile]="true"
              [color]="FontColor.INHERIT"
              [size]="TextSize.XXSMALL"
            >
              Messages
            </fl-text>
          </fl-bit>
        </ng-template>

        <fl-callout
          class="NavigationItem"
          [edgeToEdge]="true"
          [hideCloseButton]="true"
          [placement]="CalloutPlacement.BOTTOM"
          [size]="CalloutSize.MEDIUM"
        >
          <fl-callout-trigger class="NavigationItemInner">
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="Updates"
              [flShowMobile]="true"
              [display]="'flex'"
              [link]="NavLink.UPDATES"
              [ngClass]="{
                IsActive: (currentUrl$ | async) === NavLink.UPDATES
              }"
              (click)="handleUpdatesClick($event)"
            >
              <ng-container
                *ngTemplateOutlet="updatesButtonContent"
              ></ng-container>
            </fl-button>
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="Updates"
              [flHideMobile]="true"
              [display]="'flex'"
              (click)="handleUpdatesClick()"
            >
              <ng-container
                *ngTemplateOutlet="updatesButtonContent"
              ></ng-container>
            </fl-button>
            <ng-template #updatesButtonContent>
              <fl-bit class="NavigationItem-inner">
                <fl-bit
                  class="IconContainer"
                  [flHideTablet]="this.showGiveButton$ | async"
                  [flMarginRightTablet]="Margin.XXSMALL"
                >
                  <fl-icon
                    [color]="IconColor.INHERIT"
                    [name]="'ui-bell-outline'"
                  ></fl-icon>
                  <fl-unread-indicator
                    *ngIf="updatesUnread"
                    ariaLabel="unread messages"
                  ></fl-unread-indicator>
                </fl-bit>
                <fl-text
                  i18n="Navigation primary tab label"
                  [color]="FontColor.INHERIT"
                  [flHideMobile]="true"
                  [size]="TextSize.XXSMALL"
                >
                  Updates
                </fl-text>
              </fl-bit>
            </ng-template>
          </fl-callout-trigger>
          <fl-callout-content>
            <app-updates
              (anyUnread)="handleUpdatesUnread($event)"
            ></app-updates>
          </fl-callout-content>
        </fl-callout>

        <!-- Give button -->
        <!-- Not for mobile view. -->
        <ng-container *ngIf="showGiveButton$ | async">
          <fl-bit
            [flHideMobile]="true"
            class="NavigationItem"
            *flFeature="Feature.GIVE_GET"
          >
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="GiveButton"
              [display]="'flex'"
              [link]="NavLink.GIVE"
              [ngClass]="{ IsActive: (currentUrl$ | async) === NavLink.GIVE }"
            >
              <fl-bit class="NavigationItem-inner">
                <fl-bit
                  class="IconContainer"
                  [flHideTablet]="this.showGiveButton$ | async"
                  [flMarginRightTablet]="Margin.XXSMALL"
                >
                  <fl-icon
                    [color]="IconColor.INHERIT"
                    [name]="'ui-reward-gift-alt'"
                  ></fl-icon>
                </fl-bit>
                <fl-text
                  i18n="Navigation primary tab label"
                  [color]="FontColor.INHERIT"
                  [size]="TextSize.XXSMALL"
                >
                  Get {{ 20 | flCurrency: 'USD':false:false }}
                </fl-text>
              </fl-bit>
            </fl-button>
          </fl-bit>
        </ng-container>
        <ng-container *ngIf="!disablePostProjectButton">
          <fl-bit
            *ngIf="!enterpriseNavigation"
            class="NavigationItem NavigationItem--primaryCTA"
          >
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="PostProjectButton"
              [flShowMobile]="true"
              [display]="'flex'"
              [link]="NavLink.POST_PROJECT"
              [ngClass]="{
                IsActive: (currentUrl$ | async) === NavLink.POST_PROJECT
              }"
            >
              <fl-icon
                [color]="IconColor.INHERIT"
                [name]="'ui-plus-outline'"
              ></fl-icon>
            </fl-button>
            <fl-button
              i18n="Navigation primary post project button"
              flTrackingLabel="PostProjectButton"
              [flHideMobile]="true"
              [color]="ButtonColor.PRIMARY"
              [link]="'/post-project'"
              [size]="ButtonSize.SMALL"
            >
              Post a Project
            </fl-button>
          </fl-bit>
          <fl-bit
            *ngIf="enterpriseNavigation"
            class="NavigationItem NavigationItem--primaryCTA"
            [flHideMobile]="true"
          >
            <fl-button
              i18n="Navigation request enterprise demo button"
              flTrackingLabel="RequestDemoButton"
              [color]="ButtonColor.PRIMARY_PINK"
              [link]="'/enterprise/contact'"
            >
              Request a Demo
            </fl-button>
          </fl-bit>
        </ng-container>

        <fl-callout
          class="NavigationItem"
          [ngClass]="{ NoButton: disablePostProjectButton }"
          [edgeToEdge]="true"
          [hideCloseButton]="true"
          [placement]="CalloutPlacement.BOTTOM_RIGHT"
        >
          <fl-callout-trigger class="NavigationItemInner">
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="UserSettings"
              [flShowMobile]="true"
              [display]="'flex'"
              [link]="NavLink.USER"
              [ngClass]="{ IsActive: (currentUrl$ | async) === NavLink.USER }"
              (click)="$event.stopPropagation()"
            >
              <ng-container
                *ngTemplateOutlet="userSettingsButtonContent"
              ></ng-container>
            </fl-button>
            <fl-button
              class="NavigationItemBtn"
              flTrackingLabel="UserSettings"
              [flHideMobile]="true"
              [display]="'flex'"
            >
              <ng-container
                *ngTemplateOutlet="userSettingsButtonContent"
              ></ng-container>
            </fl-button>

            <ng-template #userSettingsButtonContent>
              <app-user-card
                [flShowDesktopLarge]="true"
                [balance]="defaultBalance$ | async"
                [rightIconName]="'ui-chevron-down'"
                [user]="userInfoDoc.valueChanges() | async"
              ></app-user-card>
              <fl-user-avatar
                [flHideDesktopLarge]="true"
                [size]="AvatarSize.SMALL"
                [users]="[userInfoDoc.valueChanges() | async]"
              ></fl-user-avatar>
            </ng-template>
          </fl-callout-trigger>

          <fl-callout-content>
            <app-user-settings></app-user-settings>
          </fl-callout-content>
        </fl-callout>
      </fl-container>
    </fl-bit>
  `,
  styleUrls: ['./navigation-primary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationPrimaryComponent implements OnInit, AfterViewInit {
  AvatarSize = AvatarSize;
  BackgroundColor = BackgroundColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CalloutPlacement = CalloutPlacement;
  CalloutSize = CalloutSize;
  TextSize = TextSize;
  FontColor = FontColor;
  Feature = Feature;
  IconColor = IconColor;
  LogoSize = LogoSize;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  NavLink = NavLink;

  size: ContainerSize;

  @Input()
  showGiveButton$: Rx.Observable<boolean>;

  @Input()
  darkMode: boolean;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  @Input() disablePostProjectButton = false;

  @Input() enterpriseNavigation = false;

  defaultBalance$: Rx.Observable<UserBalance>;
  messagesUnread$: Rx.Observable<boolean>;
  threads$: Rx.Observable<ReadonlyArray<Thread>>;
  updatesClick$: Rx.Observable<void>;
  updatesUnread: boolean;
  userBalances$: Rx.Observable<ReadonlyArray<UserBalance>>;
  userInfoDoc: DatastoreDocument<UserInfoCollection>;
  currentUrl$: Rx.Observable<string>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private testability: Testability,
    private performanceTracking: PerformanceTracking,
    private router: Router,
  ) {}

  ngOnInit() {
    const userId$ = this.auth.getUserId();

    this.userBalances$ = this.datastore
      .collection<UserBalancesCollection>('userBalances')
      .valueChanges()
      .pipe(
        map(balances =>
          // If the balance is 0 don't return the balance information
          // unless if the currency is the user's primary currency.
          balances.filter(balance => balance.amount !== 0 || balance.primary),
        ),
      );

    this.defaultBalance$ = this.userBalances$.pipe(
      map(userBalances => userBalances.find(balance => balance.primary)),
      filter(isDefined),
    );

    this.userInfoDoc = this.datastore.document<UserInfoCollection>(
      'userInfo',
      userId$,
    );

    this.messagesUnread$ = this.datastore
      .collection<ThreadsCollection>('threads', query => query.limit(5))
      .valueChanges()
      .pipe(map(threads => this.hasMessagesUnread(threads)));

    this.testability.whenStable(() => {
      this.performanceTracking.mark('navigation_fully_loaded', true);
    });

    this.currentUrl$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
      map(url => url.split('?')[0].split('#')[0]),
      publishReplay(1),
      refCount(),
    );
  }

  ngAfterViewInit() {
    this.performanceTracking.mark('navigation_rendered', true);
  }

  hasMessagesUnread(threads: ReadonlyArray<Thread>): boolean {
    return (
      threads
        .map(thread => thread.messageUnreadCount)
        .reduce((a, b) => a + b, 0) > 0
    );
  }

  handleUpdatesClick(event?: Event) {
    this.updatesUnread = false;
    if (event) {
      event.stopPropagation();
    }
  }

  handleUpdatesUnread(anyUnread: boolean) {
    this.updatesUnread = anyUnread;
  }
}
